/* STATUS / AGENT NOTES (2026-09-11)
 * - Removed the branded failure overlay that appeared in Meta in-app browsers.
 * - Meta browsers receive an immediate native muted-autoplay attempt; a real gesture retries a policy-blocked visible video.
 * Agent START: Codex poster-first video lifecycle with Meta in-app autoplay recovery.
 */
(() => {
  const videos = [...document.querySelectorAll('video[data-scooch-deferred-video]')];
  if (!videos.length) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const isMetaInAppBrowser = /\b(FBAN|FBAV|FBIOS|FB_IAB|FB4A|Instagram|MessengerForiOS)\b/i.test(navigator.userAgent || '');
  const states = videos.map(video => ({
    video,
    visible: false,
    ready: video.dataset.scoochDeferredVideo !== 'hero' || isMetaInAppBrowser,
    failed: false,
    blocked: false,
    manual: false,
    attempting: false,
    attemptId: 0
  }));
  let heroTimer;

  function attachSource(state) {
    const { video } = state;
    if (video.getAttribute('src')) return true;
    if (!video.dataset.scoochVideoSrc) {
      state.failed = true;
      return false;
    }
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.controls = false;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('autoplay', '');
    video.removeAttribute('controls');
    video.loop = true;
    video.src = video.dataset.scoochVideoSrc;
    return true;
  }

  function handlePlaybackFailure(state, error, attemptId) {
    if (state.attemptId !== attemptId) return;
    state.attempting = false;
    if (error?.name === 'AbortError' && (!state.visible || document.hidden)) return;
    state.blocked = true;
    state.manual = false;
  }

  function attemptPlayback(state, manual = false) {
    const { video } = state;
    if (state.attempting || !video.paused) return;
    if (!state.visible || document.hidden || state.failed) return;
    if (!manual && (!state.ready || state.blocked || reduced.matches)) return;
    if (!attachSource(state)) return;
    state.manual = manual;
    state.attempting = true;
    const attemptId = ++state.attemptId;
    let playback;
    try {
      playback = video.play();
    } catch (error) {
      handlePlaybackFailure(state, error, attemptId);
      return;
    }
    if (playback && typeof playback.then === 'function') {
      playback.then(() => {
        if (state.attemptId === attemptId) state.attempting = false;
      }, error => handlePlaybackFailure(state, error, attemptId));
    } else {
      state.attempting = false;
    }
  }

  function pauseForLifecycle(state) {
    state.attemptId += 1;
    state.attempting = false;
    state.manual = false;
    state.video.pause();
  }

  function sync(state) {
    if (!state.visible || document.hidden || (reduced.matches && !state.manual)) {
      pauseForLifecycle(state);
      return;
    }
    attemptPlayback(state);
  }

  states.forEach(state => {
    state.video.addEventListener('playing', () => {
      if (!state.visible || document.hidden || (reduced.matches && !state.manual)) {
        pauseForLifecycle(state);
        return;
      }
      state.attempting = false;
      state.failed = false;
      state.blocked = false;
    });
    state.video.addEventListener('error', () => {
      if (state.failed) return;
      state.failed = true;
      state.blocked = false;
      state.manual = false;
      state.attempting = false;
      state.attemptId += 1;
      state.video.pause();
      state.video.removeAttribute('src');
      state.video.load();
    });
  });

  const syncAll = () => states.forEach(sync);
  const retryOnGesture = event => {
    states.forEach(state => {
      if (!state.visible || document.hidden || state.attempting || !state.video.paused) return;
      const directVideoGesture = event.target === state.video || state.video.contains(event.target);
      if (!state.blocked && !(state.failed && directVideoGesture) && !(reduced.matches && directVideoGesture)) return;
      state.failed = false;
      state.blocked = false;
      attemptPlayback(state, true);
    });
  };
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const state = states.find(item => item.video === entry.target);
      if (!state) return;
      state.visible = entry.isIntersecting;
      sync(state);
    });
  }, { threshold: 0.05 }) : null;
  states.forEach(state => {
    if (observer) observer.observe(state.video);
    else state.visible = state.video.getBoundingClientRect().top < innerHeight;
  });
  function releaseHeroes() {
    states.forEach(state => { state.ready = true; });
    syncAll();
  }
  // Start after the first visible render, not after unrelated app requests finish.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    heroTimer = setTimeout(releaseHeroes, 2000);
  }));
  document.addEventListener('visibilitychange', syncAll);
  if (typeof reduced.addEventListener === 'function') reduced.addEventListener('change', syncAll);
  else if (typeof reduced.addListener === 'function') reduced.addListener(syncAll);
  // Agent: retry inside the real gesture stack; Meta WebViews can reject delayed scripted autoplay.
  ['pointerdown', 'touchstart', 'keydown'].forEach(type => document.addEventListener(type, retryOnGesture, { passive: true }));
  window.addEventListener('pagehide', () => {
    states.forEach(pauseForLifecycle);
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      clearTimeout(heroTimer);
      releaseHeroes();
    }
  });
})();
/* Agent END: Codex scoped landing video lifecycle with Meta in-app autoplay recovery. */
